import useStateMachine from '@cassiozen/usestatemachine'
import { useSubjectUpdate } from '@healthblocks-io/core'
import { AnimatePresence } from 'framer-motion'
import { useEffect, useMemo } from 'react'
import { Helmet } from 'react-helmet-async'
import { v4 as uuidv4 } from 'uuid'

import { FloatingLayout } from '@/layout/components/floating-layout'
import { useKinshipTypes } from '@/modules/additional-contacts/hooks/useKinshipTypes'
import { useLeanSubject } from '@/modules/healthblocks/hooks/useLeanSubject'
import { AddContactsBox } from './components/AddContactsBox'
import { InformationBox } from './components/InformationBox'
import { IntroductionBox } from './components/IntroductionBox'

export const AdditionalContactInformationPage = () => {
  const [{ mutate }, updateSubject] = useSubjectUpdate()

  const { subject } = useLeanSubject()

  const {
    getKinshipTypes: { execute: getKinshipTypes, value: kinshipTypes },
  } = useKinshipTypes()

  // Template variables
  // ------------------------------------------------------------------------- /

  const additionalContacts = useMemo(() => {
    return subject?.meta.additionalContacts?.map((initialContact) => ({
      ...initialContact,
      healthblocksId: initialContact.healthblocksId ?? uuidv4(),
    }))
  }, [JSON.stringify(subject?.meta.additionalContacts)])

  const [currentStep, sendCurrentStep] = useStateMachine({
    initial: 'introduction',
    states: {
      introduction: {
        on: { NEXT: 'information' },
      },
      information: {
        on: { NEXT: 'addContacts' },
      },
      addContacts: { on: { NEXT: 'done' } },
      done: {
        effect() {
          // Refresh the user before updating the actor to make sure we have the latest metadata
          mutate().then((latestSubject) =>
            updateSubject({
              meta: {
                ...latestSubject?.meta,
                hasConfirmedContactInformation: true,
              },
            }),
          )
        },
      },
    },
  })

  // Effects
  // ------------------------------------------------------------------------- /
  useEffect(() => {
    getKinshipTypes()
  }, [])

  // Interaction methods
  // ------------------------------------------------------------------------- /
  function nextStep() {
    sendCurrentStep('NEXT')
  }

  return (
    <FloatingLayout image='/img/happy-nurse.jpg' imageGridColumns='3fr 7fr'>
      <Helmet title='MijnZiekenhuis | Onboarding' />

      <AnimatePresence>
        {currentStep.value === 'introduction' ? <IntroductionBox onStartConfirmation={nextStep} /> : null}

        {currentStep.value === 'information' ? <InformationBox onContactInformationComplete={nextStep} /> : null}

        {currentStep.value === 'addContacts' ? (
          <AddContactsBox
            isFloating
            initialContacts={additionalContacts}
            kinshipTypes={kinshipTypes}
            onComplete={nextStep}
            onSkip={nextStep}
          />
        ) : null}
      </AnimatePresence>
    </FloatingLayout>
  )
}
